'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function GPACalculator() {
  const t = useTranslations("tools.gpaCalculator");  // 注意：首字母小写，嵌套在 tools 下
  const [inputs, setInputs] = useState({ courseName: '', credits: '', grade: '' });
  const [results, setResults] = useState<number | null>(null);
  const [screenSize, setScreenSize] = useState('mobile');

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    const credits = parseFloat(inputs.credits);
    const grade = parseFloat(inputs.grade);
    if (credits && grade) {
      const gpa = grade * credits / credits; // 计算 GPA
      setResults(gpa);
    }
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', padding: '1rem' },
    { padding: '1rem' }, // mobile
    { padding: '2rem' }, // tablet
    { padding: '3rem' }  // desktop
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem' },
    { fontSize: '2rem' }, // mobile
    { fontSize: '2.5rem' }, // tablet
    { fontSize: '3rem' }   // desktop
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
      padding: '1.5rem 1rem',
      marginBottom: '2rem',
      borderRadius: '1rem',
    },
    { padding: '1.5rem 1rem' }, // mobile
    { padding: '2rem 1.5rem' }, // tablet
    { padding: '3rem 2rem' }    // desktop
  );

  const inputStyle = getResponsiveStyle(
    {
      width: '100%',
      padding: '0.75rem 1rem',
      fontSize: '1rem',
      border: `2px solid ${colors.glassBorder}`,
      borderRadius: '0.5rem',
      marginBottom: '1rem',
    },
    { padding: '0.75rem 1rem' }, // mobile
    { padding: '0.875rem 1.25rem' }, // tablet
    { padding: '1rem 1.5rem' } // desktop
  );

  const calculateButtonStyle = getResponsiveStyle(
    {
      width: '100%',
      padding: '0.875rem 1.5rem',
      fontSize: '1rem',
      fontWeight: 700,
      color: '#fff',
      background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
      border: 'none',
      borderRadius: '0.5rem',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
    },
    { padding: '0.875rem 1.5rem' }, // mobile
    { padding: '1rem 2rem' }, // tablet
    { padding: '1.25rem 2.5rem' } // desktop
  );

  return (
    <div style={containerStyle}>
      <header style={{ textAlign: 'center', marginBottom: '2rem' }}>
        <h1 style={h1Style}>{t("title")}</h1>
        <p style={{ color: colors.textSecondary }}>{t("subtitle")}</p>
      </header>

      <section style={calculatorToolStyle}>
        <div style={{ maxWidth: '600px', margin: '0 auto' }}>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 600, color: colors.textPrimary }}>{t("toolTitle")}</h2>
          <form onSubmit={handleCalculate}>
            <div style={{ marginBottom: '15px' }}>
              <label htmlFor="courseName" style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px' }}>{t("inputLabel1")}</label>
              <input
                type="text"
                id="courseName"
                value={inputs.courseName}
                onChange={(e) => setInputs({ ...inputs, courseName: e.target.value })}
                style={inputStyle}
                required
              />
            </div>
            <div style={{ marginBottom: '15px' }}>
              <label htmlFor="credits" style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px' }}>{t("inputLabel2")}</label>
              <input
                type="number"
                id="credits"
                value={inputs.credits}
                onChange={(e) => setInputs({ ...inputs, credits: e.target.value })}
                style={inputStyle}
                required
              />
            </div>
            <div style={{ marginBottom: '15px' }}>
              <label htmlFor="grade" style={{ display: 'block', fontWeight: 'bold', marginBottom: '5px' }}>{t("inputLabel3")}</label>
              <select
                id="grade"
                value={inputs.grade}
                onChange={(e) => setInputs({ ...inputs, grade: e.target.value })}
                style={inputStyle}
                required
              >
                <option value="">{t("selectGrade")}</option>
                <option value="4.0">{t("gradeA")}</option>
                <option value="3.7">{t("gradeAminus")}</option>
                <option value="3.3">{t("gradeBplus")}</option>
                <option value="3.0">{t("gradeB")}</option>
                <option value="2.7">{t("gradeBminus")}</option>
                <option value="2.3">{t("gradeCplus")}</option>
                <option value="2.0">{t("gradeC")}</option>
                <option value="1.7">{t("gradeCminus")}</option>
                <option value="1.3">{t("gradeDplus")}</option>
                <option value="1.0">{t("gradeD")}</option>
                <option value="0.0">{t("gradeF")}</option>
              </select>
            </div>
            <button type="submit" style={calculateButtonStyle}>{t("calculateButton")}</button>
          </form>
          {results !== null && (
            <div style={{ marginTop: '20px', background: 'rgba(255, 255, 255, 0.9)', padding: '1rem', borderRadius: '0.75rem' }}>
              <h3>{t("resultsTitle")}</h3>
              <div>
                <span style={{ fontWeight: 'bold' }}>{t("yourGPA")}: </span>
                <span style={{ color: colors.primary }}>{results.toFixed(2)}</span>
              </div>
            </div>
          )}
        </div>
      </section>

      <section style={{ background: colors.glassBg, padding: '2rem', borderRadius: '1rem', marginBottom: '2rem' }}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: 600, color: colors.textPrimary }}>{t("introTitle")}</h2>
        <p style={{ color: colors.textSecondary }}>{t("introParagraph")}</p>
      </section>

      <section style={{ background: colors.glassBg, padding: '2rem', borderRadius: '1rem', marginBottom: '2rem' }}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: 600, color: colors.textPrimary }}>{t("howToUseTitle")}</h2>
        <ol>
          <li>{t("howToUseStep1")}</li>
          <li>{t("howToUseStep2")}</li>
          <li>{t("howToUseStep3")}</li>
        </ol>
      </section>

      <section style={{ background: colors.glassBg, padding: '2rem', borderRadius: '1rem', marginBottom: '2rem' }}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: 600, color: colors.textPrimary }}>{t("insightsTitle")}</h2>
        <p style={{ color: colors.textSecondary }}>{t("insightsContent")}</p>
      </section>

      <section style={{ background: colors.glassBg, padding: '2rem', borderRadius: '1rem', marginBottom: '2rem' }}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: 600, color: colors.textPrimary }}>{t("detailsTitle")}</h2>
        <p style={{ color: colors.textSecondary }}>{t("detailsContent")}</p>
      </section>

      <section style={{ background: colors.glassBg, padding: '2rem', borderRadius: '1rem', marginBottom: '2rem' }}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: 600, color: colors.textPrimary }}>{t("faqTitle")}</h2>
        <ul>
          <li>{t("faqQuestion1")}</li>
          <li>{t("faqQuestion2")}</li>
        </ul>
      </section>

      <section style={{ background: colors.glassBg, padding: '2rem', borderRadius: '1rem', marginBottom: '2rem' }}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: 600, color: colors.textPrimary }}>{t("referencesTitle")}</h2>
        <ul>
          <li><a href="https://turinq.com/the-ultimate-2025-gpa-guide/" target="_blank" rel="noopener noreferrer">{t("reference1")}</a></li>
          <li><a href="https://empowerly.com/applications/best-gpa-calculators-for-students/" target="_blank" rel="noopener noreferrer">{t("reference2")}</a></li>
        </ul>
      </section>
    </div>
  );
}